import type * as React from 'react';
import { graphql } from 'react-apollo';

import { apolloFetchPolicy } from 'js/utils/apolloFetchPolicy';

import getPremiumProductCollectionsByDomain from 'bundles/browse/components/Domain/queries/getPremiumProductCollectionsByDomainQuery.graphql';
import { PRODUCT_TYPE_NAMES, ProductVariantNames } from 'bundles/browse/constants';
import type { DegreeCardDegreeProps, DegreeProductMember, Partner } from 'bundles/browse/types/degree-list';
import { buildDegreeUrlAbsolute, buildMastertrackUrlAbsolute } from 'bundles/common/utils/urlUtils';
import type {
  ArticlesPremiumProductCollectionsQuery as GraphqlData,
  ArticlesPremiumProductCollectionsQueryVariables as GraphqlVariables,
  ArticlesPremiumProductCollections_LearningProductFragment as LearningProduct,
  ArticlesPremiumProductCollections_LearningProductPartnerFragment as LearningProductPartner,
} from 'bundles/unified-seo-content-common/queries/__generated__/getDegreesByCategory';
import getDegreesByCategory from 'bundles/unified-seo-content-common/queries/getDegreesByCategory.graphql';

type PremiumProductProps = DegreeProductMember & DegreeCardDegreeProps;

export type PropsFromWithDegreeCategoryCollection = {
  degreeListFromPremiumProductsCollection?: PremiumProductProps[];
  isLoading?: boolean;
};

export const allProductVariants = [ProductVariantNames.BachelorsDegree, ProductVariantNames.MastersDegree];

export const defaultVariables = {
  domains: null,
  categories: null,
  productVariants: allProductVariants,
};

export const defaultOptions = {
  ...apolloFetchPolicy({ cacheForLoggedOutOnly: false }),
  query: getPremiumProductCollectionsByDomain,
  variables: defaultVariables,
  errorPolicy: 'all' as const,
};

const buildProgramAbsoluteUrl = (slug: string, programType: string) => {
  if (programType === PRODUCT_TYPE_NAMES.DEGREE) {
    return buildDegreeUrlAbsolute(slug);
  } else {
    return buildMastertrackUrlAbsolute(slug);
  }
};

function filterNullOrUndefined<T>(x: T | null | undefined): x is T {
  return !!x;
}

function learningProductPartnerToDegreeListPartner(partner: LearningProductPartner): Partner {
  return {
    ...partner,
    squareLogo: partner.logo,
  };
}

export const getDegreeProductWithLinkAndPartners = (
  degreeProduct: LearningProduct,
  productType: string
): PremiumProductProps => {
  const primaryPartner = degreeProduct.partners?.[0];
  return {
    ...degreeProduct,
    marketingName: degreeProduct.name,
    partner: primaryPartner ? learningProductPartnerToDegreeListPartner(primaryPartner) : primaryPartner,
    partners: degreeProduct.partners
      ? {
          elements: degreeProduct.partners.filter(filterNullOrUndefined).map(learningProductPartnerToDegreeListPartner),
        }
      : undefined,
    type: productType,
    link: buildProgramAbsoluteUrl(degreeProduct.slug, productType),
    bannerImage: degreeProduct.imageUrl,
    partnerIds: [],
    domainIds: [],
  };
};

const isDegree = (product: LearningProduct) =>
  product.productVariant === ProductVariantNames.BachelorsDegree ||
  product.productVariant === ProductVariantNames.MastersDegree;

const withDegreeCategoryCollections =
  <P extends Record<string, unknown>>(getVariablesFromProps: (props: P) => GraphqlVariables, useSsr?: boolean) =>
  (BaseComponent: React.ComponentType<PropsFromWithDegreeCategoryCollection & P>) =>
    graphql<P, GraphqlData, GraphqlVariables, PropsFromWithDegreeCategoryCollection>(getDegreesByCategory, {
      options: (props) => ({
        ...defaultOptions,
        variables: {
          ...defaultVariables,
          ...getVariablesFromProps(props),
        },
        context: {
          clientName: 'gatewayGql',
        },
        ssr: useSsr ?? true,
      }),
      props: ({ data }): PropsFromWithDegreeCategoryCollection => {
        const premiumProductCollection = data?.PremiumProductCollections?.queryCollection;
        const premiumProductsRaw = premiumProductCollection || [];
        const premiumProducts: LearningProduct[] = premiumProductsRaw.filter(filterNullOrUndefined);
        const degreeList: Array<PremiumProductProps> = premiumProducts
          .filter((product) => isDegree(product))
          .map((product) => getDegreeProductWithLinkAndPartners(product, PRODUCT_TYPE_NAMES.DEGREE));

        return {
          degreeListFromPremiumProductsCollection: degreeList,
          isLoading: data?.loading,
        };
      },
    })(BaseComponent);

export default withDegreeCategoryCollections;
